import React from "react"
import * as ReactDOM from "react-dom"
import { Link } from "react-router-dom"
import { SvgAIFillGradient } from "@digits-shared/components/SVGIcons/SvgAIGradient"
import { Modal } from "@digits-shared/components/UI/Elements/Modals/Modal"
import zIndexes from "@digits-shared/DesignSystem/zIndexes"
import { useModalRoot } from "@digits-shared/hooks/useModalRoot"
import colors from "@digits-shared/themes/colors"
import fonts, { BodyText, H1Text } from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"

/*
 * STYLES
 */

const modalOverlayStyles = css`
  backdrop-filter: blur(10px);
  z-index: ${zIndexes.stickyNavMax - 1};
  margin-left: calc(var(--sidebar-content-width) - var(--sidebar-side-margin));
`

const GradientIconWrapper = styled.div<{ $size?: number }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => `${$size ?? 80}px`};
  height: ${({ $size }) => `${$size ?? 80}px`};
  border-radius: 50%;
  background: linear-gradient(84deg, rgba(57, 156, 212, 0.2) -4.07%, rgba(94, 91, 233, 0.2) 95.1%);
`

const StyledModal = styled(Modal)`
  padding: 0;
  position: relative;
  border-radius: 16px;
  border: none;
  width: 552px;
`

const SimpleModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px;
  background: ${colors.white};
  border-radius: 16px;
  gap: 16px;
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const Title = styled(H1Text)`
  text-align: center;
  font-weight: 800;
`

const Features = styled.ul`
  padding: 12px 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 16px 0;
`

const FeatureItem = styled.li`
  display: flex;
  gap: 16px;
`

const FeatureTitle = styled.div`
  font-weight: ${fonts.weight.black};
  font-size: 14px;
  color: ${colors.secondary};
`

const FeatureMessage = styled(BodyText)`
  font-size: 12px;
`

export const CalloutLink = styled(Link)`
  ${fonts.scale.body};
  font-weight: ${fonts.weight.heavy};
  color: ${colors.accentBlue};

  &:hover {
    text-decoration: underline;
  }
`

const FeatureIcon = styled(SvgAIFillGradient)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`

const HeadingIcon = styled(SvgAIFillGradient)`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`

/*
 * INTERFACES
 */

interface Feature {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  title: React.ReactNode
  message: React.ReactNode
}

export interface WelcomeModalProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  title: string
  features: Feature[]
}

/*
 * COMPONENTS
 */

const FeatureRow: React.FC<{ feature: Feature }> = ({ feature }) => (
  <FeatureItem>
    <GradientIconWrapper $size={40}>
      <FeatureIcon IconComponent={feature.icon} pathIcon />
    </GradientIconWrapper>
    <div>
      <FeatureTitle>{feature.title}</FeatureTitle>
      <FeatureMessage>{feature.message}</FeatureMessage>
    </div>
  </FeatureItem>
)

export const WelcomeModal: React.FC<React.PropsWithChildren<WelcomeModalProps>> = ({
  title,
  icon,
  features,
  children,
}) => {
  const modalRoot = useModalRoot()

  return ReactDOM.createPortal(
    <StyledModal overlayStyles={modalOverlayStyles}>
      <SimpleModalContainer>
        <GradientIconWrapper>
          <HeadingIcon IconComponent={icon} pathIcon />
        </GradientIconWrapper>
        <TextContent>
          <Title>{title}</Title>
          <Features>
            {features.map((feature, idx) => (
              <FeatureRow key={idx} feature={feature} />
            ))}
          </Features>
        </TextContent>

        {children}
      </SimpleModalContainer>
    </StyledModal>,
    modalRoot
  )
}
